import React from 'react'
import Navbar from '../components/sections/Navbar'
import Footer from '../components/sections/Footer'
import { useSEO } from '../hooks/useSEO'

export default function PrivacyPolicyPage() {
  useSEO({
    title: 'Privacy Policy | AugmentAppz',
    description: 'Learn how AugmentAppz collects, uses, and protects your personal information and data.',
  })

  return (
    <>
      <Navbar />
      <main className="legal-page">

        <div className="legal-page__hero">
          <div className="container">
            <p className="legal-page__badge">Legal</p>
            <h1 className="legal-page__title">Privacy Policy</h1>
            <p className="legal-page__subtitle">
              Learn how AugmentAppz collects, uses, and protects your personal information and data.
            </p>
          </div>
        </div>

        <div className="container legal-page__body">

          <p className="legal-page__intro">
            AUGMENTAPPZ PRIVATE LIMITED (&ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) is committed to protecting and respecting your privacy.
            This Privacy Policy outlines how we collect, use, disclose, and protect your personal information when you
            access or use our Software as a Service (&ldquo;SaaS&rdquo;) platform, services, and applications (collectively,
            &ldquo;Services&rdquo;). By accessing or using our platform, you agree to the practices described in this Privacy Policy.
          </p>

          <section className="legal-section">
            <h2 className="legal-section__heading">Definitions</h2>
            <ul className="legal-list">
              <li><strong>&ldquo;We&rdquo;, &ldquo;Our&rdquo;, and &ldquo;Us&rdquo;</strong> shall mean and refer to the Platform and/or the Firm/or the Company as the context so requires.</li>
              <li><strong>&ldquo;You&rdquo;, &ldquo;Your&rdquo;, &ldquo;Yourself&rdquo;, &ldquo;User&rdquo; and &ldquo;Customer&rdquo;</strong> shall mean and refer to natural and legal individuals who use the Platform and who are competent to enter into binding contracts, as per Indian laws.</li>
              <li><strong>&ldquo;Users&rdquo;</strong> shall refer to the individuals using either or both of the platforms.</li>
              <li><strong>&ldquo;Services&rdquo;</strong> refer to providing a Platform for Users.</li>
              <li><strong>&ldquo;Third Parties&rdquo;</strong> refer to any Application, Company, or individual apart from the User and the creator of this Platform.</li>
              <li><strong>&ldquo;Platform&rdquo;</strong> refers to the Company&apos;s Mobile Application and/or Website.</li>
              <li><strong>&ldquo;Website&rdquo;</strong> refers to the domain name https://augmentappz.ai/ which provides the User with information on the Platform and can also be used to avail of the services of the Company.</li>
              <li><strong>&ldquo;Personal Information&rdquo;</strong> shall mean and refer to any Personally Identifiable Information (PII) that We may collect from You such as Name, Location, Email ID, Mobile number, Password, Photo, etc.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">1. Information We Collect</h2>
            <p className="legal-section__text">We may collect various types of information to provide and improve our services:</p>
            <h3 className="legal-section__subheading">1.1 Personal Information</h3>
            <ul className="legal-list">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Company name</li>
              <li>Payment information (e.g., billing details, payment methods)</li>
            </ul>
            <h3 className="legal-section__subheading">1.2 Usage Data</h3>
            <ul className="legal-list">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Operating system</li>
              <li>Referring URLs, pages visited, date and time of access</li>
              <li>Location data (if location services are enabled)</li>
            </ul>
            <h3 className="legal-section__subheading">1.3 Customer Data</h3>
            <p className="legal-section__text">
              If you are using our Services to develop applications or store data on our platform, we may collect and store the applications,
              content, or other data created or uploaded by you, including any data input into our platform.
            </p>
            <h3 className="legal-section__subheading">1.4 Cookies and Tracking Technologies</h3>
            <p className="legal-section__text">
              We use cookies and other tracking technologies (such as web beacons) to track activity on our Services and hold certain
              information. Cookies are small files stored on your device that help us improve our Services, analyze usage, and customize content.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">2. How We Use Your Information</h2>
            <h3 className="legal-section__subheading">2.1 Provision of Services</h3>
            <ul className="legal-list">
              <li>To provide, operate, and maintain our SaaS platform and Services</li>
              <li>To support and enhance your experience using our Services</li>
              <li>To process transactions, including payments, and manage your subscription model</li>
            </ul>
            <h3 className="legal-section__subheading">2.2 Communication</h3>
            <ul className="legal-list">
              <li>To communicate with you about your account, service updates, and technical support</li>
              <li>To send you promotional, marketing, and informational communications, subject to your consent where required</li>
            </ul>
            <h3 className="legal-section__subheading">2.3 Improvement and Development</h3>
            <ul className="legal-list">
              <li>To monitor and analyze usage and trends, and to improve and personalize our Services</li>
              <li>To enhance security and performance, and detect and prevent fraud or malicious activities</li>
            </ul>
            <h3 className="legal-section__subheading">2.4 Legal and Regulatory Compliance</h3>
            <ul className="legal-list">
              <li>To comply with applicable laws, regulations, or legal processes</li>
              <li>To respond to legal requests or prevent harm to our rights or the rights of others</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">3. How We Share Your Information</h2>
            <p className="legal-section__text">
              We are committed to ensuring that your personal data is not sold, rented, or shared with third parties for marketing
              purposes without your explicit consent. We may share your information in the following circumstances:
            </p>
            <h3 className="legal-section__subheading">3.1 Service Providers</h3>
            <p className="legal-section__text">
              We may share your data with third-party vendors, service providers, contractors, or agents who perform services on our behalf,
              such as hosting, maintenance, payment processing, or customer support. These Service Providers are contractually bound to
              safeguard your data.
            </p>
            <h3 className="legal-section__subheading">3.2 Business Transfers</h3>
            <p className="legal-section__text">
              In the event of a merger, acquisition, or sale of all or part of our business, we may transfer your personal information as
              part of that transaction in accordance with applicable privacy laws.
            </p>
            <h3 className="legal-section__subheading">3.3 Legal Obligations</h3>
            <ul className="legal-list">
              <li>Comply with legal processes (e.g., subpoenas, court orders)</li>
              <li>Protect our rights, property, or safety</li>
              <li>Prevent or investigate potential violations of our Terms of Service</li>
              <li>Protect the rights and safety of users or the public</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">4. Data Security</h2>
            <p className="legal-section__text">
              We implement reasonable and appropriate physical, technical, and organizational measures to protect the personal information
              we collect and store. However, no method of transmission or storage can be guaranteed to be 100% secure.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">5. Data Retention</h2>
            <p className="legal-section__text">
              We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected,
              including to comply with legal obligations, resolve disputes, and enforce agreements.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">6. Your Data Rights</h2>
            <ul className="legal-list">
              <li>Access, correct, update, or request a copy of your personal data</li>
              <li>Request the deletion of your personal data</li>
              <li>Object to or restrict the processing of your personal data</li>
              <li>Withdraw your consent to the processing of your data</li>
              <li>Request data portability (in certain circumstances)</li>
            </ul>
            <p className="legal-section__text">
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:innovate@augmentappz.ai" className="legal-link">innovate@augmentappz.ai</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">7. International Data Transfers</h2>
            <p className="legal-section__text">
              By using our Services, you acknowledge that your personal information may be transferred to, and processed in, countries
              outside of your jurisdiction. In such cases, we take appropriate measures to ensure your data is protected by this Policy.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">8. Third-Party</h2>
            <h3 className="legal-section__subheading">8.1 Third-Party Services</h3>
            <p className="legal-section__text">
              Our platform may contain links to third-party websites or services not operated or controlled by us. We are not responsible
              for the privacy practices of these third parties.
            </p>
            <h3 className="legal-section__subheading">8.2 Third-Party Licenses</h3>
            <p className="legal-section__text">
              Our platform may utilize third-party open-source libraries or tools. You agree to comply with these third-party licensing
              terms while using the platform.
            </p>
            <h3 className="legal-section__subheading">8.3 Usage of Third-Party Software</h3>
            <p className="legal-section__text">
              When using our platform to develop your own applications, you acknowledge that we are not liable for any third-party software
              or libraries used within your applications.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">9. Children&apos;s Privacy</h2>
            <p className="legal-section__text">
              Our Services are not intended for children under the age of 13. If you become aware that a child under 13 has provided us
              with personal information, please contact us so we can take appropriate action.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">10. Changes to This Privacy Policy</h2>
            <p className="legal-section__text">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated
              Privacy Policy on our website or through other communication channels.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">11. Confidentiality</h2>
            <h3 className="legal-section__subheading">11.1 Confidentiality Obligations</h3>
            <p className="legal-section__text">
              Both Parties agree to maintain the confidentiality of any proprietary information and not to disclose it to any third party
              without prior written consent, except as required by law.
            </p>
            <h3 className="legal-section__subheading">11.2 Non-Disclosure</h3>
            <p className="legal-section__text">
              The receiving Party shall only use Confidential Information to perform obligations under this agreement and shall take all
              reasonable precautions to prevent unauthorized access or disclosure.
            </p>
            <h3 className="legal-section__subheading">11.3 Exceptions</h3>
            <p className="legal-section__text">
              Confidential Information does not include information that is: (a) publicly available without breach of this Agreement;
              (b) lawfully obtained from a third party; or (c) independently developed by the receiving Party.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">12. Intellectual Property (IP)</h2>
            <h3 className="legal-section__subheading">12.1 Ownership of Intellectual Property</h3>
            <p className="legal-section__text">
              All intellectual property rights in the software, solutions, tools, and technologies developed or provided by the Company
              shall remain the exclusive property of the Company.
            </p>
            <h3 className="legal-section__subheading">12.2 Customer Content and Data</h3>
            <p className="legal-section__text">
              The Customer retains ownership of all data, content, and information submitted through the use of the Company&apos;s services.
              The Customer grants the Company a worldwide, royalty-free, non-exclusive license to use, store, and process such data
              solely to provide the services.
            </p>
            <h3 className="legal-section__subheading">12.3 Third-Party Intellectual Property</h3>
            <p className="legal-section__text">
              The Company may use third-party software, libraries, or components. Such third-party intellectual property shall remain
              the property of the respective third-party owners.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">13. Severability</h2>
            <p className="legal-section__text">
              If any provision of this Privacy Policy is determined to be invalid or unenforceable, such provision shall be construed to
              reflect the original intent as closely as possible. The remainder of the Agreement shall remain in full force and effect.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">14. Amendment</h2>
            <h3 className="legal-section__subheading">14.1 Modification of Terms</h3>
            <p className="legal-section__text">
              The Company reserves the right to amend or update this Privacy Policy at any time. Changes will be communicated via the
              provided communication channels and the revised policy becomes effective on the date specified.
            </p>
            <h3 className="legal-section__subheading">14.2 Customer Consent</h3>
            <p className="legal-section__text">
              Continued use of the Company&apos;s services after any amendments constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">15. Security</h2>
            <h3 className="legal-section__subheading">15.1 Data Security</h3>
            <p className="legal-section__text">
              The Company will implement and maintain reasonable technical, administrative, and physical safeguards to protect the security
              and confidentiality of Customer Data, including encryption protocols, firewalls, access controls, and regular security audits.
            </p>
            <h3 className="legal-section__subheading">15.2 Customer Responsibilities</h3>
            <p className="legal-section__text">
              The Customer agrees to maintain the confidentiality of any login credentials and is responsible for ensuring that their
              end-user devices meet appropriate security standards.
            </p>
            <h3 className="legal-section__subheading">15.3 Notification of Breach</h3>
            <p className="legal-section__text">
              In the event of a security breach that compromises Customer Data, the Company will notify the affected Customer(s) within
              15 days of becoming aware of the breach.
            </p>
            <h3 className="legal-section__subheading">15.4 Compliance with Laws</h3>
            <p className="legal-section__text">
              The Company shall comply with applicable data protection and privacy laws including GDPR. You are responsible for ensuring
              your applications comply with all applicable laws and regulations.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">16. Payment Terms</h2>
            <p className="legal-section__text">
              Payments for services are billed in advance on a monthly or yearly basis, with auto-renewal options. If payment is not made
              within 45 days of the due date, access to the subscription will be terminated.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">17. Taxes</h2>
            <p className="legal-section__text">
              Any taxes related to the use of our services, including VAT, sales tax, or other regulatory taxes, will be the responsibility
              of the user based on their location and applicable tax laws.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">18. Creation, Publishing, and Hosting of Apps</h2>
            <p className="legal-section__text">
              Users may create, publish, and host their applications on our platform, subject to the terms of service. Hosting is available
              as part of paid subscription plans. Freemium tier users may have limitations.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">19. Trademarks and Copyrights</h2>
            <p className="legal-section__text">
              All content, logos, trademarks, and intellectual property related to the platform are owned by AUGMENTAPPZ PRIVATE LIMITED.
              You are granted a limited, non-exclusive license to use the platform in accordance with these terms.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">20. Disclaimers Regarding Generative AI Usage</h2>
            <p className="legal-section__text">
              The platform may include generative AI tools. You acknowledge that AI-generated content may not always be accurate, complete,
              or reliable. We disclaim any warranties related to AI outputs.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">21. Warranties and Disclaimers</h2>
            <p className="legal-section__text">
              To the fullest extent permitted by law, AUGMENTAPPZ PRIVATE LIMITED disclaims all warranties, express or implied, regarding
              the platform and its services, including warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">22. Changes to This Privacy Policy</h2>
            <p className="legal-section__text">
              We reserve the right to update or revise this Privacy Policy at any time. Any changes will be communicated via the platform,
              and you will be required to accept the new terms to continue using the services.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">23. Contact Us</h2>
            <p className="legal-section__text">
              If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <div className="legal-contact-card">

              {/* Company header */}
              <div className="lcc-company">
                <span className="lcc-company-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M9 21V12h6v9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span className="lcc-company-name">AUGMENTAPPZ PRIVATE LIMITED</span>
              </div>

              {/* Info rows */}
              <ul className="lcc-rows" role="list">

                <li className="lcc-row">
                  <span className="lcc-row-icon" aria-hidden="true">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <div className="lcc-row-body">
                    <span className="lcc-row-label">Email</span>
                    <a href="mailto:innovate@augmentappz.ai" className="lcc-row-value">innovate@augmentappz.ai</a>
                  </div>
                </li>

              </ul>
            </div>
          </section>

        </div>
      </main>
      <Footer />
    </>
  )
}
